import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// INTERFACES
import { PaginationConfig } from '../../../shared/configs';
import {
  IGetApiResponse,
  IGetApiResponseMeta
} from '../../../shared/interfaces';

// SERVICES
import { CommonModule } from '@angular/common';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { DirectiveModule } from '../../../shared/directives/directive.module';
import { UiModule } from '../../../ui/ui.module';
import { ChannelApiService } from '../../../shared';
import { EChannelType } from '../../contants';

// CONFIGS

@Component({
  selector: 'lum-fulfillment-channel-control',
  templateUrl: './fulfillment-channel-control.component.html',
  styleUrls: ['./fulfillment-channel-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FulfillmentChannelControlComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    DirectiveModule,
    UiModule,
  ],
})
export class FulfillmentChannelControlComponent implements OnInit {
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  loading$ = this._loading.asObservable();

  private readonly _syncLoading = new BehaviorSubject<boolean>(<boolean>false);
  syncLoading$ = this._syncLoading.asObservable();

  public fulfillmentChannels: any[] = [];
  public displayValue :string;

  private onChange :any = () => {};
  private onTouched :any = () => {};

  paginationConfig = PaginationConfig;
  queryParams :{
    [param :string] :string | string[] | boolean;
  } = {};
  meta :IGetApiResponseMeta = null;

  selectedFulfillmentChannel :Partial<any>;
  private onDestroy$ = new Subject();
  @Input() channelTypes: string[] = [EChannelType.SALES_CHANNEL, EChannelType.END_TO_END_CHANNEL];

  constructor(
    private channelApiService :ChannelApiService,
    private changeDetectorRef :ChangeDetectorRef,
  ) {}

  ngOnChanges(changes :SimpleChanges) {
  }

  writeValue(value :any) :void {
    if (!value || value === null) {
      return;
    }
    this.getById(value);
  }

  registerOnChange(fn :any) :void {
    this.onChange = fn;
  }

  registerOnTouched(fn :any) :void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled :boolean) :void {
    // You can implement this if you need to handle disabling the component
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnInit() :void {
    this.initQueryParams();
    this.getRecords();
    this._loading.subscribe((loading :boolean) => {
      if (!loading) {
        this._syncLoading.next(false);
      }
    });
  }

  onSelectEvent(selectedFulfillmentChannel: any) {
    if (this.selectedFulfillmentChannel && this.selectedFulfillmentChannel.id === selectedFulfillmentChannel.id) {
      return;
    }
    this.displayValue = selectedFulfillmentChannel.label;
    this.selectedFulfillmentChannel = selectedFulfillmentChannel;
    this.onChange(this.selectedFulfillmentChannel.id);
  }

  onChangeEvent(keyword :string) {
    this.resetValues();
    this.queryParams.page = PaginationConfig.page.toString();
    this.getRecords(keyword);
  }

  getById(id :number) {
    this.channelApiService
      .getById(id, {})
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (record: any) => {
          this.onSelectEvent(record);
          this.changeDetectorRef.detectChanges();
        },
        error: (e) => {},
      });
  }

  private initQueryParams() :void {
    this.queryParams = {
      limit: PaginationConfig.perPage.toString(),
      page: PaginationConfig.page.toString(),
      channelTypes: this.channelTypes.join(','),
      status: '1',
    };
  }

  resetValues() {
    this.onChange(null);
    this.fulfillmentChannels = [];
  }

  getRecords(searchQuery: string | null = '', synchronous: boolean = false) {
    const params: any = { ...this.queryParams };
    if (searchQuery) {
      params.label = searchQuery;
    }
    if (synchronous) {
      params.synchronous = true;
      this._syncLoading.next(true);
    }
    this._loading.next(true);

    // Check if sales_channel is in the allowed channel types
    const allowsSalesChannel = this.channelTypes.includes(EChannelType.SALES_CHANNEL);
    const allowsEndToEndChannel = this.channelTypes.includes(EChannelType.END_TO_END_CHANNEL);

    if (allowsSalesChannel) {
      // First, try loading only sales channels
      const salesChannelParams = {
        ...params,
        channelTypes: EChannelType.SALES_CHANNEL
      };

      this.channelApiService
        .getV2(salesChannelParams)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response: IGetApiResponse) => {
            if (response.data && response.data.length > 0) {
              // Found sales channels, use them
              this.fulfillmentChannels = [...this.fulfillmentChannels, ...response.data];
              this.meta = response.meta;
              this._loading.next(false);
            } else if (allowsEndToEndChannel) {
              // No sales channels found, try with sales_channel and end_to_end_channel
              const fallbackParams = {
                ...params,
                channelTypes: [EChannelType.SALES_CHANNEL, EChannelType.END_TO_END_CHANNEL].join(',')
              };

              this.channelApiService
                .getV2(fallbackParams)
                .pipe(takeUntil(this.onDestroy$))
                .subscribe({
                  next: (fallbackResponse: IGetApiResponse) => {
                    this.fulfillmentChannels = [...this.fulfillmentChannels, ...fallbackResponse.data];
                    this.meta = fallbackResponse.meta;
                    this._loading.next(false);
                  },
                  error: (e) => {
                    this._loading.next(false);
                  },
                });
            } else {
              // No sales channels and end_to_end not allowed, just return empty results
              this.fulfillmentChannels = [...this.fulfillmentChannels, ...response.data];
              this.meta = response.meta;
              this._loading.next(false);
            }
          },
          error: (e) => {
            this._loading.next(false);
          },
        });
    } else {
      // Sales channel not allowed, use the original channel types from input
      this.channelApiService
        .getV2(params)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (response: IGetApiResponse) => {
            this.fulfillmentChannels = [...this.fulfillmentChannels, ...response.data];
            this.meta = response.meta;
            this._loading.next(false);
          },
          error: (e) => {
            this._loading.next(false);
          },
        });
    }
  }

  onHardRefreshStores() :void {
    this.resetValues();
    this.getRecords(null, true);
  }

  onScrollPaginate() :void {
    if (this._loading.value || this.meta.current_page === this.meta.last_page) {
      return;
    }
    this.queryParams = {
      ...this.queryParams,
      page: (Number(this.meta.current_page) + 1).toString(),
    };
    this.getRecords();
  }
}
